import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img, { ImgProps } from "@insite/mobius/Img";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    imageUrl = "imageUrl",
    altText = "altText",
    qualification = "qualification",
    advisoryName = "advisoryName",
    advisoryBio = "advisoryBio",
    readMoreLink = "readMoreLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.advisoryName]: string;
        [fields.imageUrl]: string;
        [fields.altText]: string;
        [fields.qualification]: string;
        [fields.advisoryBio]: string;
        [fields.advisoryName]: string;
        [fields.readMoreLink]: LinkFieldValue;
    };
}
type Props = OwnProps;

export interface GaiaHomePageAdvisoryBoardstyles {
    advisoryBoardCard?: InjectableCss;
    advisoryBoardCardImgContainer?: InjectableCss;
    advisoryBoardCardContentContainer?: InjectableCss;
    advisoryBoardCardContentName?: TypographyProps;
    advisoryBoardCardContentEdu?: TypographyProps;
    advisoryBoardCardContentBio?: TypographyProps;
    advisoryBoardCardContentReadMore?: TypographyProps;
    img?: ImgProps;
}
export const gaiaHomePageAdvisoryBoardstyles: GaiaHomePageAdvisoryBoardstyles = {
    advisoryBoardCard: {
        css: css`
            padding: 28px 26px;
            display: flex;
            border-radius: 10px;
            background: white;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    advisoryBoardCardImgContainer: {
        css: css`
            width: 136px;
            height: 172px;
            margin-right: 18px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-right: 0;
                        margin-bottom: 20px;
                        height: 100%;
                        width: 100%;
                    `,
                    css`
                        margin-right: 0;
                        margin-bottom: 20px;
                        height: 100%;
                        width: 100%;
                    `,
                    css`
                        margin-right: 0;
                        margin-bottom: 20px;
                        height: 100%;
                        width: 100%;
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    img: {
        css: css`
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: top;
        `,
    },
    advisoryBoardCardContentContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 74%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 60%;
                    `,
                    css``,
                ])}
        `,
    },
    advisoryBoardCardContentName: {
        variant: "h2",
        color: "primary",
        css: css`
            font-size: 1.375rem;
            margin-bottom: 6px;
            text-transform: capitalize;
        `,
    },
    advisoryBoardCardContentEdu: {
        css: css`
            margin-bottom: 13px;
            font-weight: 700 !important;
            text-transform: capitalize;
        `,
    },
    advisoryBoardCardContentBio: {
        css: css`
            flex-grow: 1;
            font-size: 15px;
            text-overflow: ellipsis;
            display: -webkit-box;
            overflow: hidden;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            margin-bottom: 7px;
        `,
    },
    advisoryBoardCardContentReadMore: {
        css: css`
            font-weight: 700;
            margin-bottom: 0;
        `,
    },
};
const styles = gaiaHomePageAdvisoryBoardstyles;
const GaiaHomePageAdvisoryBoard: React.FC<Props> = props => {
    const { url } = useGetLink(props.fields.readMoreLink);

    return (
        <>
            <StyledWrapper {...styles.advisoryBoardCard}>
                <StyledWrapper {...styles.advisoryBoardCardImgContainer}>
                    <Img src={props.fields.imageUrl} altText={props.fields.altText} loading="lazy" {...styles.img} />
                </StyledWrapper>
                <StyledWrapper {...styles.advisoryBoardCardContentContainer}>
                    <Typography {...styles.advisoryBoardCardContentName}>{props.fields.advisoryName}</Typography>

                    <Typography as="p" {...styles.advisoryBoardCardContentEdu}>
                        {props.fields.qualification}
                    </Typography>

                    <Typography as="p" {...styles.advisoryBoardCardContentBio}>
                        {props.fields.advisoryBio}
                    </Typography>

                    <Typography as="p" {...styles.advisoryBoardCardContentReadMore}>
                        <Link href={url || "#"}>Read More</Link>
                    </Typography>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            name: fields.imageUrl,
            fieldType: "General",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
        },
        {
            name: fields.altText,
            fieldType: "General",
            editorTemplate: "TextField",
            defaultValue: "",
        },
        {
            name: fields.advisoryName,
            displayName: "Name",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "text tip",
            placeholder: "Add Name",
            isRequired: true,
        },
        {
            name: fields.qualification,
            displayName: "Qualification",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "multi line tip",
            placeholder: "multi line placeholder",
            isRequired: true,
        },
        {
            name: fields.advisoryBio,
            displayName: "Description",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "multi line tip",
            placeholder: "multi line placeholder",
            isRequired: true,
        },
        {
            name: fields.readMoreLink,
            fieldType: "General",
            editorTemplate: "LinkField",
            defaultValue: { type: "Page", value: "" },
        },
    ],
};
const widgetModule: WidgetModule = {
    component: GaiaHomePageAdvisoryBoard,
    definition,
};

export default widgetModule;
